import React from 'react'
import PropTypes from 'prop-types'
import { inject, observer } from 'mobx-react'

import { BLOCK_TYPES, FIELD_TYPES } from '@client/components/Block/constants'
import { NULL as BlockModelNULL } from '@client/components/Block/model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import AsyncComponent from '@client/components/AsyncComponent'
import Loading from '@client/components/PageLoading'
import { cancelStorage } from '@client/utils/Storage'

import './styles.scss'

export const TextSettings = AsyncComponent({
  component: () => import('./TextSettings'),
  loading: Loading,
})

export const ImageSettings = AsyncComponent({
  component: () => import('./ImageSettings'),
  loading: Loading,
})

export const VideoSettings = AsyncComponent({
  component: () => import('./VideoSettings'),
  loading: Loading,
})

export const SectionSettings = AsyncComponent({
  component: () => import('./SectionSettings'),
  loading: Loading,
})

export const SpacingSettings = AsyncComponent({
  component: () => import('./SpacingSettings'),
  loading: Loading,
})

export const ButtonSettings = AsyncComponent({
  component: () => import('./ButtonSettings'),
  loading: Loading,
})

export const SimpleButtonSettings = AsyncComponent({
  component: () => import('./SimpleButtonSettings'),
  loading: Loading,
})

export const SubmitButtonSettings = AsyncComponent({
  component: () => import('./SubmitButtonSettings'),
  loading: Loading,
})

export const ConfirmButtonSettings = AsyncComponent({
  component: () => import('./ConfirmButtonSettings'),
  loading: Loading,
})

export const SocialSettings = AsyncComponent({
  component: () => import('./SocialSettings'),
  loading: Loading,
})

export const SocialSettingsV2 = AsyncComponent({
  component: () => import('./SocialSettingsV2'),
  loading: Loading,
})

export const DividerSettings = AsyncComponent({
  component: () => import('./DividerSettings'),
  loading: Loading,
})

export const HtmlSettings = AsyncComponent({
  component: () => import('./HtmlSettings'),
  loading: Loading,
})

export const FormSettings = AsyncComponent({
  component: () => import('./FormSettings'),
  loading: Loading,
})

export const ShortTextSettings = AsyncComponent({
  component: () => import('./ShortTextSettings'),
  loading: Loading,
})

export const LongTextSettings = AsyncComponent({
  component: () => import('./LongTextSettings'),
  loading: Loading,
})

export const ScaleSettings = AsyncComponent({
  component: () => import('./ScaleSettings'),
  loading: Loading,
})

export const SelectOneSettings = AsyncComponent({
  component: () => import('./SelectOneSettings'),
  loading: Loading,
})

export const SelectManySettings = AsyncComponent({
  component: () => import('./SelectManySettings'),
  loading: Loading,
})

export const TimerSettings = AsyncComponent({
  component: () => import('./TimerSettings'),
  loading: Loading,
})

export const BannerSettings = AsyncComponent({
  component: () => import('./BannerSettings'),
  loading: Loading,
})

export const ProductSettings = AsyncComponent({
  component: () => import('./ProductSettings'),
  loading: Loading,
})

export const SidebarSettings = ({
  app = AppModelNULL,
  editor = EditorModelNULL,
}) => {
  return (
    <div className="sidebar-settings">
      <Choose>
        <When condition={app.editingSettings.type === BLOCK_TYPES.TEXT}>
          <TextSettings model={app.editingSettings} editor={editor} />
        </When>
        <When condition={app.editingSettings.type === BLOCK_TYPES.HTML}>
          <HtmlSettings model={app.editingSettings} />
        </When>
        <When condition={app.editingSettings.type === BLOCK_TYPES.FORM}>
          <FormSettings model={app.editingSettings} editor={editor} />
        </When>
        <When condition={app.editingSettings.type === BLOCK_TYPES.IMAGE}>
          <ImageSettings model={app.editingSettings} />
        </When>
        <When condition={app.editingSettings.type === BLOCK_TYPES.VIDEO}>
          <VideoSettings model={app.editingSettings} />
        </When>
        <When condition={app.editingSettings.type === BLOCK_TYPES.SECTION}>
          <SectionSettings model={app.editingSettings} />
        </When>
        <When condition={app.editingSettings.type === BLOCK_TYPES.SPACING}>
          <SpacingSettings model={app.editingSettings} />
        </When>
        <When condition={app.editingSettings.type === BLOCK_TYPES.BUTTON}>
          <ButtonSettings model={app.editingSettings} />
        </When>
        <When
          condition={app.editingSettings.type === BLOCK_TYPES.SIMPLE_BUTTON}
        >
          <SimpleButtonSettings model={app.editingSettings} />
        </When>
        <When
          condition={app.editingSettings.type === BLOCK_TYPES.SUBMIT_BUTTON}
        >
          <SubmitButtonSettings model={app.editingSettings} />
        </When>
        <When
          condition={app.editingSettings.type === BLOCK_TYPES.CONFIRM_BUTTON}
        >
          <ConfirmButtonSettings model={app.editingSettings} />
        </When>
        <When
          condition={app.editingSettings.type === BLOCK_TYPES.TIMER}
        >
          <TimerSettings model={app.editingSettings} />
        </When>
        <When
          condition={app.editingSettings.type === BLOCK_TYPES.BANNER}
        >
          <BannerSettings model={app.editingSettings} />
        </When>
        <When
          condition={app.editingSettings.type === BLOCK_TYPES.PRODUCT}
        >
          <ProductSettings model={app.editingSettings} editor={editor} />
        </When>
        <When
          condition={
            app.editingSettings.type === BLOCK_TYPES.SHARE ||
            app.editingSettings.type === BLOCK_TYPES.FOLLOW
          }
        >
          <SocialSettings model={app.editingSettings} />
        </When>
        <When
          condition={
            app.editingSettings.type === BLOCK_TYPES.SHARE_V2 ||
            app.editingSettings.type === BLOCK_TYPES.FOLLOW_V2
          }
        >
          <SocialSettingsV2 model={app.editingSettings} />
        </When>
        <When condition={app.editingSettings.type === BLOCK_TYPES.DIVIDER}>
          <DividerSettings model={app.editingSettings} />
        </When>
        <When
          condition={
            app.editingSettings.type === BLOCK_TYPES.FIELD &&
            app.editingSettings.fieldType === FIELD_TYPES.SHORT_TEXT
          }
        >
          <ShortTextSettings model={app.editingSettings} />
        </When>
        <When
          condition={
            app.editingSettings.type === BLOCK_TYPES.FIELD &&
            app.editingSettings.fieldType === FIELD_TYPES.LONG_TEXT
          }
        >
          <LongTextSettings model={app.editingSettings} />
        </When>
        <When
          condition={
            app.editingSettings.type === BLOCK_TYPES.FIELD &&
            app.editingSettings.fieldType === FIELD_TYPES.SCALE
          }
        >
          <ScaleSettings model={app.editingSettings} />
        </When>
        <When
          condition={
            app.editingSettings.type === BLOCK_TYPES.FIELD &&
            app.editingSettings.fieldType === FIELD_TYPES.SELECT_ONE
          }
        >
          <SelectOneSettings model={app.editingSettings} />
        </When>
        <When
          condition={
            app.editingSettings.type === BLOCK_TYPES.FIELD &&
            app.editingSettings.fieldType === FIELD_TYPES.SELECT_MANY
          }
        >
          <SelectManySettings model={app.editingSettings} />
        </When>
      </Choose>
      <div className="sidebar-footer" style={{ width: app.editingSettings.isCroppingFullWidth ? '50vw' : '380px' }}>
        <a
          role="button"
          className="bp6-button ml-10 sidebar-footer__cancel-btn"
          tabIndex="1"
          onClick={async () => {
            const storedItem = cancelStorage.getItem()
            if (storedItem) {
              editor.amend(storedItem)
              await editor.save()
              cancelStorage.clear()
            }
            app.setEditingSettings(BlockModelNULL)
          }}
        >
          {app.formatMessage('sidebarsettings.cancelBtn')}
        </a>
        <a
          role="button"
          className={
            `bp6-button bp6-intent-success ${editor.error ? 'bp6-disabled' : ''}`
          }
          tabIndex="0"
          onClick={async () => {
            app.setEditingSettings(BlockModelNULL)
            await editor.save()
            cancelStorage.clear()
          }}
        >
          {app.formatMessage('sidebarsettings.saveAndReturnBtn')}
        </a>
      </div>
    </div>
  )
}

SidebarSettings.propTypes = {
  app: PropTypes.instanceOf(AppModel),
  editor: PropTypes.instanceOf(EditorModel),
}

export default inject('app', 'editor')(observer(SidebarSettings))
